import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { DataTable, columnTypes, ratio, PITCH_TYPES, dateTranslate } from 'yankees-react-utils';
import moment from 'moment';
import ReportNote from '../../../components/ReportNote';

const Container = styled.div`
    margin-bottom: 20px;
    > * {
        margin-bottom: 20px;
    }
    && .table-striped p {
        margin: 0;
    }
`;
const SuperHeaderRow = styled.tr`
    background-color: #C6D3DC;
`;
const SuperHeaderCell = styled.th`
    && {
        border-left: 1px solid #FFF;
        border-right: 1px solid #FFF;
        background-color: #244C74 !important;
        color: #FFF;
    }
`;

const buildTotals = (translate, seasonPitchUsage) => {
    const pitchTypes = PITCH_TYPES(translate).reduce((acc, p) => ({ ...acc, [p.id]: p }), {});
    const strengthsByType = seasonPitchUsage.reduce((acc, s, idx) => {
        if (!acc[s.fxtype]) {
            acc[s.fxtype] = {
                ...s,
                pitchtype: pitchTypes[s.fxtype],
                ervera: {},
                sortOrder: idx, // SP already sorts by usage. Can add front-end sorting if necessary
            };
        }
        if (s.levelid === 8 || !s.levelid) acc[s.fxtype].ervera[8] = s.ervera;
        else if (s.levelid !== 8) acc[s.fxtype].ervera[7] = s.ervera;
        return acc;
    }, {});

    const numpitches = { total: 0, early: 0, late: 0, threetwo: 0 };
    Object.keys(strengthsByType).map((pt) => {
        const pitch = strengthsByType[pt];
        numpitches.total += pitch.numpitches;
        numpitches.early += pitch.numpitches_early;
        numpitches.late += pitch.numpitches_late;
        numpitches.threetwo += pitch.numpitches_32;
        return pitch;
    }).sort((a, b) => a.sortOrder - b.sortOrder);
    return numpitches;
};

const PitchUsage = ({ gameId, yearId, startDate, endDate, pitchType, playerId, ppfxData, pitchUsageLhh, pitchUsageRhh }) => {
    const { t: translate } = useTranslation();
    const pitcherPitchTypes = [pitchType];

    let rows = [];
    let columns = [];

    const lhhTotals = buildTotals(translate, pitchUsageLhh);
    const rhhTotals = buildTotals(translate, pitchUsageRhh);

    const hasGameData = gameId && Object.keys(ppfxData).length > 0;
    if (hasGameData) {
        const { gameline: [game], pitches } = ppfxData;
        const { gamedate } = game;
        const gamePitches = pitches.filter((p) => p.gameid === gameId);

        const gameTotalLhhAttack = gamePitches.filter((p) => p.islhb && p.strikes < 2).length;
        const gameTotalLhhTwoStrike = gamePitches.filter((p) => p.islhb && p.strikes === 2).length;
        const gameTotalRhhAttack = gamePitches.filter((p) => !p.islhb && p.strikes < 2).length;
        const gameTotalRhhTwoStrike = gamePitches.filter((p) => !p.islhb && p.strikes === 2).length;

        rows = pitcherPitchTypes.map((type) => {
            const gamePitchesOfType = gamePitches
                .filter((p) => (type.pitchtype === 1 ? (p.pitchtype === 9 || p.pitchtype === 10) : (p.pitchtype === type.pitchtype && p.fxsubtype === type.fxsubtype)));

            const gameLhhAttack = gamePitchesOfType.filter((p) => p.islhb && p.strikes < 2).length;
            const gameLhhTwoStrike = gamePitchesOfType.filter((p) => p.islhb && p.strikes === 2).length;
            const gameRhhAttack = gamePitchesOfType.filter((p) => !p.islhb && p.strikes < 2).length;
            const gameRhhTwoStrike = gamePitchesOfType.filter((p) => !p.islhb && p.strikes === 2).length;

            const actualUsageLhh = pitchUsageLhh.find((u) => u.fxtype === type.pitchtype && u.fxsubtype === type.fxsubtype) || {};
            const seasonLhhAttack = actualUsageLhh.numpitches_early;
            const seasonLhhTwoStrike = actualUsageLhh.numpitches_late;
            const actualUsageRhh = pitchUsageRhh.find((u) => u.fxtype === type.pitchtype && u.fxsubtype === type.fxsubtype) || {};
            const seasonRhhAttack = actualUsageRhh.numpitches_early;
            const seasonRhhTwoStrike = actualUsageRhh.numpitches_late;

            return {
                gameDate: gamedate,
                yearId,
                type,
                lhhGameAttack: ratio(gameLhhAttack, gameTotalLhhAttack),
                lhhSeasonAttack: ratio(seasonLhhAttack, lhhTotals.early),
                lhhGameTwoStrike: ratio(gameLhhTwoStrike, gameTotalLhhTwoStrike),
                lhhSeasonTwoStrike: ratio(seasonLhhTwoStrike, lhhTotals.late),
                rhhGameAttack: ratio(gameRhhAttack, gameTotalRhhAttack),
                rhhSeasonAttack: ratio(seasonRhhAttack, rhhTotals.early),
                rhhGameTwoStrike: ratio(gameRhhTwoStrike, gameTotalRhhTwoStrike),
                rhhSeasonTwoStrike: ratio(seasonRhhTwoStrike, rhhTotals.late),
            };
        });

        columns = [
            // LHH Attack
            { id: 'lhhGameAttack', title: gamedate, type: columnTypes.PERCENT },
            { id: 'lhhSeasonAttack', title: yearId, type: columnTypes.PERCENT },
            {
                id: 'lhhTargetAttack',
                title: translate('sections.reports.pitcher.target'),
                type: columnTypes.NODE,
                transform: () => <ReportNote type="pts" section={11} fxtype={pitchType.pitchtype} fxsubtype={pitchType.fxsubtype} playerId={playerId} />,
            },
            // LHH Two Strike
            { id: 'lhhGameTwoStrike', title: gamedate, type: columnTypes.PERCENT },
            { id: 'lhhSeasonTwoStrike', title: yearId, type: columnTypes.PERCENT },
            {
                id: 'lhhTargetTwoStrike',
                title: translate('sections.reports.pitcher.target'),
                type: columnTypes.NODE,
                transform: () => <ReportNote type="pts" section={12} fxtype={pitchType.pitchtype} fxsubtype={pitchType.fxsubtype} playerId={playerId} />,
            },
            // RHH Attack
            { id: 'rhhGameAttack', title: gamedate, type: columnTypes.PERCENT },
            { id: 'rhhSeasonAttack', title: yearId, type: columnTypes.PERCENT },
            {
                id: 'rhhTargetAttack',
                title: translate('sections.reports.pitcher.target'),
                type: columnTypes.NODE,
                transform: () => <ReportNote type="pts" section={21} fxtype={pitchType.pitchtype} fxsubtype={pitchType.fxsubtype} playerId={playerId} />,
            },
            // RHH Two Strike
            { id: 'rhhGameTwoStrike', title: gamedate, type: columnTypes.PERCENT },
            { id: 'rhhSeasonTwoStrike', title: yearId, type: columnTypes.PERCENT },
            {
                id: 'rhhTargetTwoStrike',
                title: translate('sections.reports.pitcher.target'),
                type: columnTypes.NODE,
                transform: () => <ReportNote type="pts" section={22} fxtype={pitchType.pitchtype} fxsubtype={pitchType.fxsubtype} playerId={playerId} />,
            },
        ];
    } else {
        rows = pitcherPitchTypes.map((type) => {
            const actualUsageLhh = pitchUsageLhh.find((u) => u.fxtype === type.pitchtype && u.fxsubtype === type.fxsubtype) || {};
            const seasonLhhAttack = actualUsageLhh.numpitches_early;
            const seasonLhhTwoStrike = actualUsageLhh.numpitches_late;
            const actualUsageRhh = pitchUsageRhh.find((u) => u.fxtype === type.pitchtype && u.fxsubtype === type.fxsubtype) || {};
            const seasonRhhAttack = actualUsageRhh.numpitches_early;
            const seasonRhhTwoStrike = actualUsageRhh.numpitches_late;

            return {
                startDate,
                endDate,
                type,
                lhhSeasonAttack: ratio(seasonLhhAttack, lhhTotals.early),
                lhhSeasonTwoStrike: ratio(seasonLhhTwoStrike, lhhTotals.late),
                rhhSeasonAttack: ratio(seasonRhhAttack, rhhTotals.early),
                rhhSeasonTwoStrike: ratio(seasonRhhTwoStrike, rhhTotals.late),
            };
        });

        columns = [
            // LHH Attack
            { id: 'lhhSeasonAttack', title: `${dateTranslate(translate, moment(startDate))} - ${dateTranslate(translate, moment(endDate))}`, type: columnTypes.PERCENT, headerClass: 'text-center', cellClass: 'text-center' },
            {
                id: 'lhhTargetAttack',
                title: translate('sections.reports.pitcher.target'),
                type: columnTypes.NODE,
                transform: () => <ReportNote type="pts" section={11} fxtype={pitchType.pitchtype} fxsubtype={pitchType.fxsubtype} playerId={playerId} />,
                headerClass: 'text-center',
                cellClass: 'text-center',
            },
            // LHH Two Strike
            { id: 'lhhSeasonTwoStrike', title: `${dateTranslate(translate, moment(startDate))} - ${dateTranslate(translate, moment(endDate))}`, type: columnTypes.PERCENT, headerClass: 'text-center', cellClass: 'text-center' },
            {
                id: 'lhhTargetTwoStrike',
                title: translate('sections.reports.pitcher.target'),
                type: columnTypes.NODE,
                transform: () => <ReportNote type="pts" section={12} fxtype={pitchType.pitchtype} fxsubtype={pitchType.fxsubtype} playerId={playerId} />,
                headerClass: 'text-center',
                cellClass: 'text-center',
            },
            // RHH Attack
            { id: 'rhhSeasonAttack', title: `${dateTranslate(translate, moment(startDate))} - ${dateTranslate(translate, moment(endDate))}`, type: columnTypes.PERCENT, headerClass: 'text-center', cellClass: 'text-center' },
            {
                id: 'rhhTargetAttack',
                title: translate('sections.reports.pitcher.target'),
                type: columnTypes.NODE,
                transform: () => <ReportNote type="pts" section={21} fxtype={pitchType.pitchtype} fxsubtype={pitchType.fxsubtype} playerId={playerId} />,
                headerClass: 'text-center',
                cellClass: 'text-center',
            },
            // RHH Two Strike
            { id: 'rhhSeasonTwoStrike', title: `${dateTranslate(translate, moment(startDate))} - ${dateTranslate(translate, moment(endDate))}`, type: columnTypes.PERCENT, headerClass: 'text-center', cellClass: 'text-center' },
            {
                id: 'rhhTargetTwoStrike',
                title: translate('sections.reports.pitcher.target'),
                type: columnTypes.NODE,
                transform: () => <ReportNote type="pts" section={22} fxtype={pitchType.pitchtype} fxsubtype={pitchType.fxsubtype} playerId={playerId} />,
                headerClass: 'text-center',
                cellClass: 'text-center',
            },
        ];
    }

    const colSpan = hasGameData ? 3 : 2;
    const superheader = (
        <SuperHeaderRow>
            {!pitchType && <SuperHeaderCell>&nbsp;</SuperHeaderCell>}
            <SuperHeaderCell className="text-center" colSpan={colSpan}>{translate('sections.reports.pitcher.attack')} {translate('baseball.abbr-versus')} {translate('baseball.lhh')}</SuperHeaderCell>
            <SuperHeaderCell className="text-center" colSpan={colSpan}>{translate('sections.reports.pitcher.two-strike')} {translate('baseball.abbr-versus')} {translate('baseball.lhh')}</SuperHeaderCell>
            <SuperHeaderCell className="text-center" colSpan={colSpan}>{translate('sections.reports.pitcher.attack')} {translate('baseball.abbr-versus')} {translate('baseball.rhh')}</SuperHeaderCell>
            <SuperHeaderCell className="text-center" colSpan={colSpan}>{translate('sections.reports.pitcher.two-strike')} {translate('baseball.abbr-versus')} {translate('baseball.rhh')}</SuperHeaderCell>
        </SuperHeaderRow>
    );

    return (
        <Container>
            <DataTable
                sortable={false}
                superheader={superheader}
                columns={columns}
                rows={rows}
            />
        </Container>
    );
};

PitchUsage.propTypes = {
    gameId: PropTypes.number,
    yearId: PropTypes.number,
    startDate: PropTypes.instanceOf(Date),
    endDate: PropTypes.instanceOf(Date),
    pitchType: PropTypes.shape({
        pitchtype: PropTypes.number,
        fxsubtype: PropTypes.number,
    }),
    playerId: PropTypes.number.isRequired,
    ppfxData: PropTypes.shape({
        gameline: PropTypes.arrayOf(PropTypes.object),
        pitches: PropTypes.arrayOf(PropTypes.object),
    }),
    pitchUsageLhh: PropTypes.arrayOf(PropTypes.object).isRequired,
    pitchUsageRhh: PropTypes.arrayOf(PropTypes.object).isRequired,
};

PitchUsage.defaultProps = {
    gameId: null,
    yearId: null,
    pitchType: null,
    ppfxData: {},
    startDate: null,
    endDate: null,
};

export default PitchUsage;
